import { useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { LoadingSVG } from '../button/LoadingSVG'
import { SettingsDropdown } from './SettingsDropdown'
import { SimulateLeaseModal } from '../modals/SimulateLeaseModal'
import { SimulateDelinquencyModal } from '../modals/SimulateDelinquencyModal'
import { SimulateEmergencyModal } from '../modals/SimulateEmergencyModal'
import { SimulateShowMojoLeadModal } from '../modals/SimulateShowMojoLeadModal'
import { useConfig } from '../../hooks/useConfig'
import { ConnectionState } from 'livekit-client'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronDown } from 'lucide-react'

// LiveKit Logo SVG
function LKLogo() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_101_119699)">
        <path
          d="M19.2006 12.7998H12.7996V19.2008H19.2006V12.7998Z"
          fill="currentColor"
        />
        <path
          d="M25.6014 6.40137H19.2004V12.8024H25.6014V6.40137Z"
          fill="currentColor"
        />
        <path
          d="M25.6014 19.2002H19.2004V25.6012H25.6014V19.2002Z"
          fill="currentColor"
        />
        <path d="M32 0H25.599V6.401H32V0Z" fill="currentColor" />
        <path d="M32 25.5986H25.599V31.9996H32V25.5986Z" fill="currentColor" />
        <path
          d="M6.401 25.599V19.2005V12.7995V6.401V0H0V6.401V12.7995V19.2005V25.599V32H6.401H12.7995H19.2005V25.599H12.7995H6.401Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="clip0_101_119699">
          <rect width="32" height="32" fill="white" />
        </clipPath>
      </defs>
    </svg>
  )
}

type PlaygroundHeaderProps = {
  logo?: ReactNode
  title?: ReactNode
  height: number
  accentColor: string
  connectionState: ConnectionState
  onConnectClicked: () => void
}

export const PlaygroundHeader = ({
  logo,
  title,
  accentColor,
  height,
  onConnectClicked,
  connectionState,
}: PlaygroundHeaderProps) => {
  const { config } = useConfig()
  return (
    <div
      className={`flex gap-4 pt-4 text-${accentColor}-500 justify-between items-center shrink-0`}
      style={{
        height: height + 'px',
      }}
    >
      <div className="flex items-center gap-3 basis-2/3">
        <div className="flex lg:basis-1/2">
          <a href="https://livekit.io">{logo ?? <LKLogo />}</a>
        </div>
        <div
          className="lg:basis-1/2 lg:text-center text-xs lg:text-base lg:font-semibold text-foreground"
          style={{ fontFamily: 'var(--font-space-grotesk)' }}
        >
          {title}
        </div>
      </div>
      <div className="flex basis-1/3 justify-end items-center gap-2">
        {config.settings.editable && <SettingsDropdown />}
        <SimulateButton />

        <Button
          variant={
            connectionState === ConnectionState.Connected
              ? 'destructive'
              : 'form'
          }
          disabled={connectionState === ConnectionState.Connecting}
          onClick={async e => {
            e.preventDefault()
            e.stopPropagation()
            console.log(
              '[PlaygroundHeader] Connect button clicked, connectionState:',
              connectionState,
            )
            try {
              await onConnectClicked()
            } catch (error) {
              console.error(
                '[PlaygroundHeader] Error in onConnectClicked:',
                error,
              )
            }
          }}
        >
          {connectionState === ConnectionState.Connecting ? (
            <LoadingSVG />
          ) : connectionState === ConnectionState.Connected ? (
            'Disconnect'
          ) : (
            'Connect'
          )}
        </Button>
      </div>
    </div>
  )
}

function SimulateButton() {
  const [showLeaseModal, setShowLeaseModal] = useState(false)
  const [showDelinquencyModal, setShowDelinquencyModal] = useState(false)
  const [showEmergencyModal, setShowEmergencyModal] = useState(false)
  const [showShowMojoLeadModal, setShowShowMojoLeadModal] = useState(false)

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="gap-1 whitespace-nowrap">
            Simulate
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[120px]">
          <DropdownMenuItem onClick={() => setShowLeaseModal(true)}>
            Lease
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowDelinquencyModal(true)}>
            Delinquency
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowEmergencyModal(true)}>
            Emergency
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowShowMojoLeadModal(true)}>
            ShowMojo: Webhook Lead
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <SimulateLeaseModal
        open={showLeaseModal}
        onOpenChange={setShowLeaseModal}
      />
      <SimulateDelinquencyModal
        open={showDelinquencyModal}
        onOpenChange={setShowDelinquencyModal}
      />
      <SimulateEmergencyModal
        open={showEmergencyModal}
        onOpenChange={setShowEmergencyModal}
      />
      <SimulateShowMojoLeadModal
        open={showShowMojoLeadModal}
        onOpenChange={setShowShowMojoLeadModal}
      />
    </>
  )
}
